import React, { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Easing, StyleSheet, Text, View } from "react-native";

import { themes } from "@/Components/Styling/themeConfigs";
import { Spacings } from "@/styling/spacings";
import { getHoursMinutesSecondsDelta } from "@/formatting/datetime";
import { ExploreWord, getExploreHeaderWords } from "@/utils/explore";
import { RecommendationCategory } from "@/schemas/user/explore";

type Props = {
  categories: RecommendationCategory[];
  nextRefreshTime: Date;
  isRefreshing: boolean;
};

function ExploreFeedScreenHeader({
  categories,
  nextRefreshTime,
  isRefreshing,
}: Props): React.JSX.Element {
  const currentTheme = themes.morning;

  const exploreRotatingWords: ExploreWord[] = useMemo(() => {
    return getExploreHeaderWords(categories);
  }, [categories]);

  // countdown
  const [now, setNow] = useState(new Date());
  const isRefreshAvailable = now >= nextRefreshTime;

  // update now every second
  useEffect(() => {
    if (isRefreshAvailable) return; // stop ticking when refresh is available
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [isRefreshAvailable]);

  // ... loading dots animation
  const [loadingDotsText, setLoadingDotsText] = useState(".");

  useEffect(() => {
    const loadingInterval = setInterval(() => {
      setLoadingDotsText((prev) => {
        if (prev === "...") return ".";
        return prev + ".";
      });
    }, 500); // Change every 500ms

    return () => clearInterval(loadingInterval);
  }, []);

  // rotating words
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const fadeAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const wordAnimationInterval = setInterval(() => {
      // Fade out
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
        easing: Easing.out(Easing.ease),
      }).start(() => {
        // Change word
        setCurrentWordIndex(
          (prevIndex) => (prevIndex + 1) % exploreRotatingWords.length,
        );

        // Fade in
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
          easing: Easing.in(Easing.ease),
        }).start();
      });
    }, 3000); // Change word every 3 seconds

    return () => clearInterval(wordAnimationInterval);
  }, [fadeAnim, exploreRotatingWords.length]);

  const currentWord = exploreRotatingWords[currentWordIndex] ?? {
    text: "Match",
    color: "#000",
  };

  const styles = StyleSheet.create({
    container: {
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "center",
      paddingHorizontal: Spacings.s3,
      paddingTop: Spacings.s2,
      marginBottom: Spacings.s2,
      width: "100%",
    },
    titleContainer: {
      flexDirection: "column",
      width: "100%",
    },
    centerRow: {
      flexDirection: "row",
      justifyContent: "center",
      width: "100%",
    },
    titleText: {
      fontSize: currentTheme.typography.h1.size.mobile + 8,
      fontWeight: "bold",
      marginBottom: Spacings.s2,
    },
    subtitle: {
      fontSize: currentTheme.typography.body.size.mobile + 2,
    },
    italic: {
      fontStyle: "italic",
    },
    rotatingContainer: {
      alignItems: "center",
      justifyContent: "center",
      minWidth: 100, // Fixed width to prevent content shifting
    },
    timeRemainingContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    timeRemainingUnitContainer: {
      minWidth: 60,
      alignItems: "center",
    },
    kaiLogoBlue: {
      color: "#005294", // this is the color picked from Kai logo's blue
    },
  });

  function renderCountdownComponent(): React.ReactNode {
    const timeDelta = getHoursMinutesSecondsDelta(nextRefreshTime, now);

    return (
      <>
        <Text style={styles.titleText}>Refreshes in </Text>
        <View style={styles.timeRemainingContainer}>
          <View style={styles.timeRemainingUnitContainer}>
            <Text style={[styles.titleText, styles.kaiLogoBlue, styles.italic]}>
              {timeDelta.minutes}m{" "}
            </Text>
          </View>
          <View style={styles.timeRemainingUnitContainer}>
            <Text style={[styles.titleText, styles.kaiLogoBlue, styles.italic]}>
              {timeDelta.seconds.toString().padStart(2, "0")}s
            </Text>
          </View>
        </View>
      </>
    );
  }

  // render
  return (
    <View style={styles.container}>
      <View style={styles.titleContainer}>
        <View style={styles.centerRow}>
          <Text style={styles.titleText}>Who&apos;s Your </Text>
          <Animated.View
            style={[styles.rotatingContainer, { opacity: fadeAnim }]}
          >
            <Text
              style={[
                styles.titleText,
                styles.italic,
                { color: currentWord.color },
              ]}
            >
              {currentWord.text}
            </Text>
          </Animated.View>
          <Text style={styles.titleText}>Today?</Text>
        </View>
        <View style={styles.centerRow}>
          {isRefreshAvailable ? (
            <>
              {isRefreshing ? (
                <Text style={[styles.titleText, styles.kaiLogoBlue]}>
                  Refreshing{loadingDotsText}
                </Text>
              ) : (
                <Text style={[styles.titleText, styles.kaiLogoBlue]}>
                  Pull Down to Refresh...
                </Text>
              )}
            </>
          ) : (
            renderCountdownComponent()
          )}
        </View>
      </View>
      <Text style={styles.subtitle}>
        Connect through shared interests and community
      </Text>
    </View>
  );
}

export default ExploreFeedScreenHeader;
